import React, { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Card, IconButton, InputAdornment, ListSubheader, MenuItem, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TableSortLabel, TextField,
  Typography,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { ReadinessChip } from '../merchants/shared/StateChip'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { fmtDate, num } from '../ptl-merchant-setup/constants'
import { Banner } from '../ptl-merchant-setup/components/SetupUi'
import PtlPage from '../ptl-merchant-setup/shell/PtlPage'
import { errorText, fetchOdaCouriers, fetchOdaShipments } from './ptlOdaApi'
import { ODA_EYEBROW, ODA_ROUTES, SHIPMENT_FILTERS, SOURCE, odaTabs } from './constants'

const PAGE_SIZES = [50, 100, 200]
const day = (d) => (d ? fmtDate(d).split(',')[0] : '—')

/** Where the shipment's ODA was read, as ops read it. */
const ODA_FROM = { COURIER_ZONE_MAP: 'Courier zone map', MERCHANT_PRICING: 'Merchant pricing' }

/**
 * PTL Operations → ODA Management → ODA Shipments (PTL_ODA_REWORK_DEV_LOG.md): open PTL shipments to ODA pincodes of
 * all courier codes (default) or one, earliest EDD first or by courier, with why each is ODA (the carrying code's zone
 * map row: ODA type or km band) and the ODA days a new EDD would get. The cards filter the list. ?code=, ?filter=, ?q=
 * and ?sort= keep the view in the link. Read only.
 */
export default function PtlOdaShipments() {
  const [params, setParams] = useSearchParams()
  const [couriers, setCouriers] = useState(null)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(PAGE_SIZES[0])
  const [search, setSearch] = useState(params.get('q') || '')

  const filter = SHIPMENT_FILTERS.includes(params.get('filter')) ? params.get('filter') : 'all'
  const q = params.get('q') || ''
  const sort = params.get('sort') === 'courier' ? 'courier' : 'edd'
  // blank = all courier codes (the default view)
  const code = params.get('code') || ''
  const who = code || 'all couriers'

  const setParam = (k, v) => {
    const next = new URLSearchParams(params)
    if (v && !(k === 'filter' && v === 'all') && !(k === 'sort' && v === 'edd')) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
    setPage(0)
  }

  useEffect(() => {
    fetchOdaCouriers().then(setCouriers).catch((e) => setError(errorText(e, 'Could not load the couriers')))
  }, [])

  const load = useCallback(() => {
    setError('')
    fetchOdaShipments({ code, filter, q, sort, page, size })
      .then(setData)
      .catch((e) => { setData(null); setError(errorText(e, 'Could not load the ODA shipments')) })
  }, [code, filter, q, sort, page, size])
  useEffect(() => { setData(null); load() }, [load])

  const kpi = (key, label, value, sub, tone) => ({
    key, label, value: data ? num(value) : '', sub, tone, active: filter === key, onClick: () => setParam('filter', filter === key ? 'all' : key),
  })
  const kpis = [
    kpi('all', 'Open ODA shipments', data?.odaShipments, data ? `Of ${num(data.openShipments)} open PTL shipments${code ? ` of ${code}` : ''} booked in the last ${data.days} days` : 'Open PTL shipments to ODA pincodes'),
    kpi('type', 'On an ODA type', data?.withType, 'ODA 1 / 2 / 3 days'),
    kpi('band', 'On a km band', data?.withBand, 'No ODA type: the band’s days'),
    kpi('hand', 'Days set by hand', data?.byHand, 'On the zone map row'),
    kpi('fallback', 'On default', data?.onFallback, data?.pincodesOnFallback ? `${num(data.pincodesOnFallback)} pincodes with no type or band days` : 'No type or band days', data?.onFallback ? 'warn' : 'ok'),
    kpi('pastEdd', 'Past EDD', data?.pastEdd, 'EDD is before today', data?.pastEdd ? 'danger' : 'ok'),
  ]
  const applySearch = () => setParam('q', search.trim())

  return (
    <PtlPage title='ODA Management' tabTitle='ODA Shipments' eyebrow={ODA_EYEBROW} tabs={odaTabs()}
      description={`Open PTL shipments going to ODA pincodes${data?.days ? `, booked in the last ${data.days} days (the window tracking still updates)` : ''}, earliest EDD first. ODA is read the way the EDD job reads it: the carrying courier code’s own zone map row (ODA type or km band), else the order’s merchant pricing. ODA days are what the EDD job gives now; a shipment’s EDD takes them at its next tracking update if that moves it later.`}
      kpis={kpis} kpisLoading={!data && !error} kpisLabel={`ODA shipments of ${who}`}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {data?.capped && <Banner tone='warn'>There are more open shipments of {who} than the screen reads at once: counts and the list cover the most recent {num(data.openShipments)}. Pick a courier code to see all of its shipments.</Banner>}

      <Card elevation={0} sx={{ ...SX.card, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
          <TextField select size='small' label='Courier code' value={code} onChange={(e) => { setParam('code', e.target.value); setSearch('') }}
            sx={{ width: { xs: '100%', sm: 300 }, bgcolor: C.surface }} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value=''>All couriers</MenuItem>
            {(couriers || []).flatMap((c) => [
              <ListSubheader key={`h-${c.parent}`} sx={{ ...TYPE.eyebrow, fontSize: 10, lineHeight: '28px', color: C.inkFaint }}>{c.parent}</ListSubheader>,
              ...c.codes.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>),
            ])}
          </TextField>
          <TextField size='small' placeholder='AWB, reference or pincode' value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applySearch() }} onBlur={applySearch}
            sx={{ width: { xs: '100%', sm: 280 }, bgcolor: C.surface }} inputProps={{ 'aria-label': 'Find by AWB, reference or pincode' }}
            InputProps={{
              startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: C.inkGhost }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position='end'>
                  <IconButton size='small' aria-label='Clear search' edge='end' onClick={() => { setSearch(''); setParam('q', '') }}><ClearIcon fontSize='small' /></IconButton>
                </InputAdornment>
              ) : null,
            }} />
          <Typography role='status' sx={{ ...TYPE.rowHelp, color: C.inkMuted }}>
            {data ? `${num(data.total)} shipment${data.total === 1 ? '' : 's'}${filter !== 'all' || q ? ' in this view' : ''}` : 'Loading shipments…'}
          </Typography>
        </Box>

        {data && data.rows.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <EmptyState dense title={data.odaShipments ? 'No shipment in this view' : `No open shipment of ${who} goes to an ODA pincode`}
              body={data.odaShipments ? 'Pick another card above, or clear the search.' : `${num(data.openShipments)} open PTL shipments${code ? ` of ${code}` : ''}, none to an ODA pincode.`} />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 1180 }} aria-label={`Open shipments of ${who} to ODA pincodes`}>
              <TableHead>
                <TableRow>
                  {[['AWB'], ['Courier', 'courier'], ['Merchant'], ['Drop'], ['ODA'], ['Status'], ['Picked up'], ['EDD', 'edd'], ['ODA days'], ['']].map(([h, key], i) => (
                    <TableCell key={i} sortDirection={key && sort === key ? 'asc' : false}
                      sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>
                      {key ? (
                        <TableSortLabel active={sort === key} direction='asc' onClick={() => setParam('sort', key)}>{h}</TableSortLabel>
                      ) : h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!data && <TableRow><TableCell colSpan={10} sx={{ ...TYPE.rowHelp, textAlign: 'center', py: 3 }}>{error ? '—' : 'Loading shipments…'}</TableCell></TableRow>}
                {(data?.rows || []).map((r) => (
                  <TableRow key={r.awb} sx={{ '& td': { borderColor: C.lineSoft, py: 1 } }}>
                    <TableCell>
                      <Typography sx={{ ...TYPE.rowLabel, whiteSpace: 'nowrap' }}>{r.awb}</Typography>
                      {r.reference && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{r.reference}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody, whiteSpace: 'nowrap' }}>
                      {r.parent}
                      <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{r.code}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody, maxWidth: 200 }}>{r.merchant || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody }}>
                      {r.pincode}
                      <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{[r.city, r.state].filter(Boolean).join(', ')}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody, whiteSpace: 'nowrap' }}>
                      {r.odaType || (r.bandLabel ? `Band ${r.bandLabel}` : 'ODA')}
                      <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{ODA_FROM[r.odaFrom] || r.odaFrom}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody }}>{r.status || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, whiteSpace: 'nowrap' }}>{day(r.pickedUpAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography component='span' sx={{ fontSize: 12.5, color: r.pastEdd ? C.danger : C.inkBody, fontWeight: r.pastEdd ? 700 : 400 }}>{day(r.edd)}</Typography>
                      {r.pastEdd && <Box component='span' sx={{ ml: 0.75 }}><ReadinessChip tone='blocked' label='Past EDD' /></Box>}
                    </TableCell>
                    <TableCell sx={{ minWidth: 190 }}>
                      <Box sx={{ whiteSpace: 'nowrap' }}>
                        <Box component='span' sx={{ ...TYPE.value, fontSize: 13, mr: 1 }}>+{r.days}</Box>
                        <ReadinessChip tone={SOURCE[r.source].tone} label={r.source === 'BAND' && r.bandLabel ? r.bandLabel : SOURCE[r.source].label} />
                      </Box>
                      {r.rule && <Typography sx={{ ...TYPE.meta, color: C.inkMuted, mt: 0.25 }}>{r.rule}</Typography>}
                    </TableCell>
                    <TableCell align='right'>
                      {r.odaFrom === 'COURIER_ZONE_MAP' && (
                        <Button size='small' component={RouterLink} to={`${ODA_ROUTES.pincodes}?code=${encodeURIComponent(r.code)}&pincode=${r.pincode}`}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12.5, minHeight: 32, whiteSpace: 'nowrap' }}>
                          View pincode
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {data && data.total > 0 && (
          <TablePagination component='div' count={data.total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={PAGE_SIZES} sx={{ borderTop: `1px solid ${C.line}` }} />
        )}
      </Card>
    </PtlPage>
  )
}
